import { brainActionCanSend, type BrainAction } from "./brainAction.ts";
import { BRAIN_WORKSPACE_RELATIVE_PATH } from "./brainWorkspace.ts";

/**
 * What a Brain composer has gathered besides its text, in the order the turn
 * will carry it. The composer's chips and its ordinary attachments both land
 * here, so the rule that turns a collection into an arranged prompt is stated
 * once instead of once per client.
 */
export interface BrainComposerCollection {
  /**
   * Every attachment the turn carries, in turn order, so an index into this
   * array is the `attachmentIndex` the agent sees. Images come first because
   * the send path builds its attachment list images-then-paths.
   */
  readonly attachments: ReadonlyArray<BrainComposerAttachment>;
  /** Add text chips, in chip-row order. */
  readonly textSources: ReadonlyArray<{ readonly id: string; readonly text: string }>;
  /** Delete Raws chips: capture folder names, without the `raw/` prefix. */
  readonly deleteTargets: ReadonlyArray<string>;
}

export interface BrainComposerAttachment {
  /** Stable per-source identity; Ingest verification maps it to one raw capture. */
  readonly id: string;
  readonly name: string;
  /** A folder ingests as a tree, a file as one capture. */
  readonly isFolder: boolean;
  /**
   * The real host path when the attachment references one (ADR 0109). Absent
   * for material whose bytes travel, which the agent reads from the turn.
   */
  readonly hostPath?: string | undefined;
}

export const EMPTY_BRAIN_COMPOSER_COLLECTION: BrainComposerCollection = {
  attachments: [],
  textSources: [],
  deleteTargets: [],
};

const BRAIN_DELETE_TARGET_PREFIX = "raw/";

/** A Delete target as the contract addresses it: Brain-root-relative. */
export function brainDeleteTargetPath(captureFolder: string): string {
  return `${BRAIN_DELETE_TARGET_PREFIX}${captureFolder}`;
}

/** Where the Delete Raws picker looks, relative to the checkout root. */
export const BRAIN_RAW_RELATIVE_PREFIX = `${BRAIN_WORKSPACE_RELATIVE_PATH}/raw/`;

/**
 * What `brainActionCanSend` counts. Lint collects nothing at all, so it counts
 * zero and sends on the Action alone.
 */
export function countBrainComposerCollected(
  action: BrainAction,
  collection: BrainComposerCollection,
): number {
  if (action === "ingest") {
    return collection.attachments.length + usableBrainTextSources(collection).length;
  }
  if (action === "delete") return collection.deleteTargets.length;
  return 0;
}

/** One capture folder as the Delete Raws picker lists it. */
export interface BrainCaptureFolder {
  /** The folder name under `raw/`, which `brainDeleteTargetPath` addresses. */
  readonly name: string;
  /** Files captured under it. The only count the vault can answer without an index. */
  readonly fileCount: number;
}

/**
 * The capture folders under `raw/`, derived from a generic project listing
 * rather than a Brain-side index (ADR 0031). A folder is the distinct first
 * segment under the raw prefix, so it appears whether the listing carries the
 * directory entry itself or only the files inside it.
 */
export function listBrainCaptureFolders(
  entries: ReadonlyArray<{ readonly path: string; readonly kind: "file" | "directory" }>,
): ReadonlyArray<BrainCaptureFolder> {
  const fileCounts = new Map<string, number>();
  for (const entry of entries) {
    if (!entry.path.startsWith(BRAIN_RAW_RELATIVE_PREFIX)) continue;
    const relative = entry.path.slice(BRAIN_RAW_RELATIVE_PREFIX.length);
    const name = relative.split("/")[0];
    if (name === undefined || name.length === 0) continue;
    const counted = fileCounts.get(name) ?? 0;
    fileCounts.set(name, counted + (entry.kind === "file" ? 1 : 0));
  }
  return [...fileCounts]
    .map(([name, fileCount]) => ({ name, fileCount }))
    .toSorted((left, right) => left.name.localeCompare(right.name));
}

/** A text chip the user emptied carries nothing, so it is not a source. */
export function usableBrainTextSources(collection: BrainComposerCollection) {
  return collection.textSources.filter((source) => source.text.trim().length > 0);
}

/**
 * Whether a composer can send, with the Brain rule applied when a Brain Action
 * is picked. Without one the composer's own content rule stands; with one, the
 * Action decides — Lint sends on the Action alone, and Ingest and Delete send
 * on what they gathered rather than on typed text.
 *
 * Stated once because both the composer's send control and the send path that
 * finally dispatches have to agree: a button that sends into a refusal, or
 * refuses what the button offered, is the same bug twice.
 */
export function brainAwareComposerCanSend(input: {
  readonly action: BrainAction | null;
  readonly collection: BrainComposerCollection;
  readonly hasComposerContent: boolean;
}): boolean {
  if (input.action === null) return input.hasComposerContent;
  return brainActionCanSend({
    action: input.action,
    collectedCount: countBrainComposerCollected(input.action, input.collection),
  });
}
